import { useState } from 'react'
import { useI18n } from '../../i18n'
import type { TablatureColumn } from '../../types/songbook'

interface GuitarTablatureProps {
  columns: TablatureColumn[]
  onChange?: (columns: TablatureColumn[]) => void
}

const STRING_LABELS = ['e', 'B', 'G', 'D', 'A', 'E']
const COL_WIDTH = 28
const ROW_HEIGHT = 22
const LABEL_WIDTH = 20
const FONT_SIZE = 13

export function GuitarTablature({ columns, onChange }: GuitarTablatureProps) {
  const { t } = useI18n()
  const isEditing = !!onChange
  const [editCell, setEditCell] = useState<{ col: number; str: number } | null>(null)
  const [showImport, setShowImport] = useState(false)

  const svgW = LABEL_WIDTH + Math.max(columns.length, 1) * COL_WIDTH + (isEditing ? COL_WIDTH : 0)
  const svgH = 6 * ROW_HEIGHT + 8

  const handleCellTap = (col: number, str: number) => {
    if (!isEditing) return

    // If tapping a cell that already has a value → clear it (toggle)
    const current = columns[col]?.frets[str]
    if (current !== null && current !== undefined) {
      const newCols = columns.map((c, i) => {
        if (i !== col) return c
        const frets = [...c.frets]
        frets[str] = null
        return { frets }
      })
      onChange!(newCols)
      setEditCell(null)
      return
    }

    setEditCell({ col, str })
  }

  const handleCellInput = (value: string) => {
    if (!onChange || !editCell) return

    const num = value === '' ? null : parseInt(value, 10)
    if (num !== null && (isNaN(num) || num < 0 || num > 24)) return

    const newCols = columns.map((c, i) => {
      if (i !== editCell.col) return c
      const frets = [...c.frets]
      frets[editCell.str] = num
      return { frets }
    })

    onChange(newCols)

    // Auto-advance: next string down, or next column
    if (editCell.str < 5) {
      setEditCell({ col: editCell.col, str: editCell.str + 1 })
    } else if (editCell.col < columns.length - 1) {
      setEditCell({ col: editCell.col + 1, str: 0 })
    } else {
      setEditCell(null)
    }
  }

  const handleAddColumn = () => {
    if (!onChange) return
    onChange([...columns, { frets: [null, null, null, null, null, null] }])
  }

  const handleDeleteColumn = (idx: number) => {
    if (!onChange) return
    onChange(columns.filter((_, i) => i !== idx))
  }

  const handleImport = (text: string) => {
    if (!onChange) return
    const parsed = parseTabText(text)
    if (parsed.length > 0) {
      onChange([...columns, ...parsed])
    }
    setShowImport(false)
  }

  return (
    <div className="w-full">
      {/* Scrollable tab display */}
      <div className="overflow-x-auto pb-1 scrollbar-none">
        <svg
          viewBox={`0 0 ${svgW} ${svgH}`}
          width={svgW}
          height={svgH}
          className="select-none"
        >
          {/* String lines */}
          {STRING_LABELS.map((_, s) => {
            const y = s * ROW_HEIGHT + ROW_HEIGHT
            return (
              <line
                key={`line-${s}`}
                x1={LABEL_WIDTH}
                y1={y}
                x2={svgW}
                y2={y}
                stroke="#555"
                strokeWidth={1}
              />
            )
          })}

          {/* String labels */}
          {STRING_LABELS.map((label, s) => (
            <text
              key={`label-${s}`}
              x={LABEL_WIDTH - 4}
              y={s * ROW_HEIGHT + ROW_HEIGHT + 4}
              fontSize={11}
              fill="#888"
              textAnchor="end"
            >
              {label}
            </text>
          ))}

          {/* Column data */}
          {columns.map((col, c) => {
            const x = LABEL_WIDTH + c * COL_WIDTH + COL_WIDTH / 2

            return col.frets.map((fret, s) => {
              const y = s * ROW_HEIGHT + ROW_HEIGHT
              const isActive = editCell?.col === c && editCell?.str === s

              if (fret === null && !isActive) {
                // Empty cell tap target
                return isEditing ? (
                  <rect
                    key={`tap-${c}-${s}`}
                    x={x - COL_WIDTH / 2}
                    y={y - ROW_HEIGHT / 2}
                    width={COL_WIDTH}
                    height={ROW_HEIGHT}
                    fill="transparent"
                    className="cursor-pointer"
                    onClick={() => handleCellTap(c, s)}
                  />
                ) : null
              }

              return (
                <g key={`cell-${c}-${s}`}>
                  {/* Background to cover the line */}
                  <rect
                    x={x - 8}
                    y={y - 8}
                    width={16}
                    height={16}
                    fill="var(--color-surface-light, #2a2a3e)"
                    rx={2}
                  />
                  {/* Fret number */}
                  <text
                    x={x}
                    y={y + 5}
                    fontSize={FONT_SIZE}
                    fill={isActive ? '#3b82f6' : '#e2e8f0'}
                    fontWeight="bold"
                    textAnchor="middle"
                    className={`tab-fret-num ${isEditing ? 'cursor-pointer' : ''}`}
                    onClick={() => handleCellTap(c, s)}
                  >
                    {fret ?? ''}
                  </text>
                  {/* Tap target */}
                  {isEditing && (
                    <rect
                      x={x - COL_WIDTH / 2}
                      y={y - ROW_HEIGHT / 2}
                      width={COL_WIDTH}
                      height={ROW_HEIGHT}
                      fill="transparent"
                      className="cursor-pointer"
                      onClick={() => handleCellTap(c, s)}
                    />
                  )}
                </g>
              )
            })
          })}

          {/* Empty column placeholder for visual spacing */}
          {isEditing && (
            <g />
          )}
        </svg>
      </div>

      {/* Cell input (appears when a cell is tapped) */}
      {editCell && (
        <div className="mt-1 flex items-center gap-2 px-1">
          <span className="text-xs text-text-muted">
            {STRING_LABELS[editCell.str]}:{editCell.col + 1}
          </span>
          <input
            type="number"
            min={0}
            max={24}
            className="w-14 rounded border border-surface-lighter bg-surface px-2 py-1 text-center text-sm text-text focus:border-primary focus:outline-none"
            autoFocus
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCellInput((e.target as HTMLInputElement).value)
              } else if (e.key === 'Escape') {
                setEditCell(null)
              } else if (e.key === 'ArrowUp' && editCell.str > 0) {
                e.preventDefault()
                setEditCell({ col: editCell.col, str: editCell.str - 1 })
              } else if (e.key === 'ArrowDown' && editCell.str < 5) {
                e.preventDefault()
                setEditCell({ col: editCell.col, str: editCell.str + 1 })
              } else if (e.key === 'ArrowLeft' && editCell.col > 0) {
                e.preventDefault()
                setEditCell({ col: editCell.col - 1, str: editCell.str })
              } else if (e.key === 'ArrowRight' && editCell.col < columns.length - 1) {
                e.preventDefault()
                setEditCell({ col: editCell.col + 1, str: editCell.str })
              } else if (e.key === 'Backspace' && (e.target as HTMLInputElement).value === '') {
                handleCellInput('')
              }
            }}
            onBlur={(e) => {
              if (e.target.value !== '') handleCellInput(e.target.value)
              setEditCell(null)
            }}
          />
          <button
            className="rounded px-1.5 py-0.5 text-xs text-text-muted hover:text-error"
            onClick={() => {
              handleDeleteColumn(editCell.col)
              setEditCell(null)
            }}
          >
            Spalte -
          </button>
          <button
            className="rounded px-1.5 py-0.5 text-xs text-text-muted hover:text-text"
            onClick={() => setEditCell(null)}
          >
            OK
          </button>
        </div>
      )}

      {/* Editor controls */}
      {isEditing && !editCell && (
        <div className="mt-1 flex gap-2 px-1">
          <button
            className="rounded bg-surface-lighter px-2.5 py-1 text-xs font-medium text-text hover:bg-primary hover:text-white"
            onClick={handleAddColumn}
          >
            + Spalte
          </button>
          <button
            className="rounded px-2 py-1 text-xs text-text-muted hover:bg-surface-lighter hover:text-text"
            onClick={() => setShowImport(!showImport)}
          >
            {t('songbook.tablatureImport')}
          </button>
        </div>
      )}

      {/* Text import */}
      {showImport && (
        <div className="mt-2 px-1">
          <textarea
            rows={7}
            placeholder={'e|--0--1--3--|\nB|--2--3--0--|\nG|--2--2--0--|\nD|--2--0--0--|\nA|--0-----2--|\nE|--------3--|'}
            className="w-full rounded-lg border-2 border-surface-lighter bg-surface px-3 py-2 font-mono text-xs text-text placeholder-text-muted focus:border-primary focus:outline-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handleImport((e.target as HTMLTextAreaElement).value)
              }
            }}
          />
          <div className="mt-1 flex gap-2">
            <button
              className="rounded-lg bg-primary px-3 py-1 text-xs font-medium text-white active:bg-primary-dark"
              onClick={(e) => {
                const textarea = (e.target as HTMLElement).parentElement?.previousElementSibling as HTMLTextAreaElement
                if (textarea) handleImport(textarea.value)
              }}
            >
              {t('songbook.add')}
            </button>
            <button
              className="rounded px-2 py-1 text-xs text-text-muted"
              onClick={() => setShowImport(false)}
            >
              {t('general.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** Parse standard tab text format into columns */
function parseTabText(text: string): TablatureColumn[] {
  const lines = text.trim().split('\n')

  // Find the 6 string lines (look for patterns like "e|", "B|", etc.)
  const stringLines: string[] = []
  const labels = ['e', 'B', 'G', 'D', 'A', 'E']

  for (const label of labels) {
    const found = lines.find((l) => {
      const trimmed = l.trim()
      return trimmed.toLowerCase().startsWith(label.toLowerCase() + '|') ||
             trimmed.toLowerCase().startsWith(label.toLowerCase() + ' |')
    })
    if (found) {
      // Extract the part after the pipe
      const pipeIdx = found.indexOf('|')
      stringLines.push(found.slice(pipeIdx + 1))
    } else {
      stringLines.push('')
    }
  }

  if (stringLines.every((l) => !l)) return []

  // Find max length
  const maxLen = Math.max(...stringLines.map((l) => l.length))

  // Parse character by character, extracting numbers
  const columns: TablatureColumn[] = []
  let pos = 0

  while (pos < maxLen) {
    const frets: (number | null)[] = []
    let hasAny = false

    for (let s = 0; s < 6; s++) {
      const line = stringLines[s]
      if (pos >= line.length) {
        frets.push(null)
        continue
      }

      const ch = line[pos]
      if (ch >= '0' && ch <= '9') {
        // Check for two-digit numbers
        const next = pos + 1 < line.length ? line[pos + 1] : ''
        if (next >= '0' && next <= '9') {
          frets.push(parseInt(ch + next, 10))
        } else {
          frets.push(parseInt(ch, 10))
        }
        hasAny = true
      } else {
        frets.push(null)
      }
    }

    if (hasAny) {
      columns.push({ frets })
    }

    pos++
  }

  return columns
}
